/**
 * Production chunk-load recovery.
 *
 * A Vite deploy changes hashed JS chunk names. An already-open tab can still
 * hold the previous app shell and then request a chunk that no longer exists.
 * That presents as "Failed to fetch dynamically imported module" and can make
 * every lazy route appear broken at once.
 *
 * Recovery has three layers:
 *   1. detect the browser/Vite chunk-load error;
 *   2. perform one cache-busting navigation per tab/recovery window;
 *   3. never reload-loop when the chunk is genuinely broken.
 */

const RECOVERY_AT_KEY = "nuvra:chunk-recovery-at";
const RECOVERY_WINDOW_MS = 15_000;
const RECOVERY_QUERY = "__nuvra_chunk_recovery";

export function isChunkLoadError(err: unknown): boolean {
  const msg =
    err instanceof Error
      ? `${err.name}: ${err.message}`
      : typeof err === "string"
        ? err
        : "";

  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk [\w-]+ failed|dynamically imported module/i.test(
    msg,
  );
}

function readRecoveryAt(): number | null {
  try {
    const value = Number(sessionStorage.getItem(RECOVERY_AT_KEY));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

function writeRecoveryAt(timestamp: number): void {
  try {
    sessionStorage.setItem(RECOVERY_AT_KEY, String(timestamp));
  } catch {
    /* sessionStorage can be unavailable in restrictive/private contexts. */
  }
}

function buildCacheBustedUrl(): string {
  const url = new URL(window.location.href);
  url.searchParams.set(RECOVERY_QUERY, String(Date.now()));
  return url.toString();
}

/**
 * Removes the temporary recovery query parameter after the fresh application
 * shell has booted. Existing query parameters are preserved.
 */
export function clearChunkRecoveryMarker(): void {
  if (typeof window === "undefined") return;

  const url = new URL(window.location.href);
  if (!url.searchParams.has(RECOVERY_QUERY)) return;

  url.searchParams.delete(RECOVERY_QUERY);
  window.history.replaceState(window.history.state, "", url.toString());
}

/**
 * Recover from a chunk-load failure.
 *
 * Returns true when the error has been handled (including when another recovery
 * navigation is already in progress) and false for ordinary runtime errors.
 * The optional navigate callback exists for deterministic unit testing.
 */
export function recoverFromChunkLoadError(
  err: unknown,
  navigate: () => void = () => window.location.replace(buildCacheBustedUrl()),
): boolean {
  if (!isChunkLoadError(err)) return false;

  const now = Date.now();
  const previous = readRecoveryAt();

  // A Vite preloadError and the lazy import rejection can fire for the same
  // failure. Treat the second signal as already handled rather than starting a
  // second navigation.
  if (previous !== null && now - previous < RECOVERY_WINDOW_MS) {
    return true;
  }

  writeRecoveryAt(now);
  navigate();
  return true;
}

/**
 * Installs global recovery for Vite's preload errors plus browsers that surface
 * the same failure as an unhandled promise rejection. Safe to call once during
 * application boot.
 */
export function installChunkLoadRecovery(): () => void {
  if (typeof window === "undefined") return () => undefined;

  const onPreloadError = (event: Event) => {
    event.preventDefault();
    const payload = (event as Event & { payload?: unknown }).payload;
    recoverFromChunkLoadError(payload ?? event);
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    if (!isChunkLoadError(event.reason)) return;
    event.preventDefault();
    recoverFromChunkLoadError(event.reason);
  };

  window.addEventListener("vite:preloadError", onPreloadError);
  window.addEventListener("unhandledrejection", onUnhandledRejection);

  return () => {
    window.removeEventListener("vite:preloadError", onPreloadError);
    window.removeEventListener("unhandledrejection", onUnhandledRejection);
  };
}
